import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './App.css';
import ProjectForm from './components/ProjectForm';
import ProjectList from './components/ProjectList';
import ImageUpload from './components/ImageUpload';
import ImageGallery from './components/ImageGallery';
import Login from './components/Login';
import { formatBytes } from './lib/format';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function App() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [images, setImages] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [stats, setStats] = useState(null);
  const [projectQuery, setProjectQuery] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);

  const dismissToast = () => {
    setError(null);
    setSuccess(null);
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/stats`);
      setStats(response.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/projects`);
      setProjects(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch projects');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchImages = useCallback(async (projectId) => {
    try {
      const response = await axios.get(`${API_URL}/uploads/${projectId}`);
      setImages(response.data.images || []);
    } catch (err) {
      console.error('Failed to fetch images:', err);
      setImages([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/auth/me`)
      .then((response) => {
        if (!cancelled) setUser(response.data.user || 'admin');
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setAuthReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    fetchProjects();
    fetchStats();
    return undefined;
  }, [user, fetchProjects, fetchStats]);

  useEffect(() => {
    if (selectedProject) {
      fetchImages(selectedProject.id);
    } else {
      setImages([]);
    }
  }, [selectedProject, fetchImages]);

  useEffect(() => {
    if (!error && !success) return undefined;
    const timer = setTimeout(dismissToast, 4200);
    return () => clearTimeout(timer);
  }, [error, success]);

  const refreshAll = async (projectId) => {
    await Promise.all([fetchProjects(), fetchStats()]);
    if (projectId) await fetchImages(projectId);
  };

  const handleProjectCreate = async (projectData) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/projects`, projectData);
      setProjects((prev) => [response.data, ...prev]);
      setSelectedProject(response.data);
      setShowCreateForm(false);
      setSuccess(`Project “${response.data.name}” created`);
      setError(null);
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const handleStartRename = (project) => {
    setEditingProject(project);
    setShowCreateForm(false);
  };

  const handleProjectUpdate = async (projectData) => {
    if (!editingProject) return;
    try {
      setLoading(true);
      const response = await axios.put(
        `${API_URL}/projects/${editingProject.id}`,
        projectData
      );
      setProjects((prev) =>
        prev.map((p) => (p.id === editingProject.id ? response.data : p))
      );
      if (selectedProject?.id === editingProject.id) {
        setSelectedProject(response.data);
      }
      setEditingProject(null);
      setSuccess(`Renamed to “${response.data.name}”`);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update project');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (files) => {
    if (!selectedProject) {
      setError('Select a project before uploading');
      return;
    }
    const list = Array.isArray(files) ? files : [files];
    try {
      setLoading(true);
      for (let i = 0; i < list.length; i += 1) {
        setUploadProgress({ current: i + 1, total: list.length });
        const formData = new FormData();
        formData.append('image', list[i]);
        const response = await axios.post(
          `${API_URL}/uploads/${selectedProject.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        if (i === list.length - 1) {
          navigator.clipboard.writeText(response.data.url).catch(() => {});
        }
      }
      setSuccess(list.length > 1 ? `${list.length} images uploaded` : 'Image uploaded · URL copied');
      setError(null);
      await refreshAll(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const handleDeleteImage = async (filename) => {
    if (!selectedProject) return;
    try {
      await axios.delete(`${API_URL}/uploads/${selectedProject.id}/${filename}`);
      setSuccess('Image removed');
      await refreshAll(selectedProject.id);
    } catch (err) {
      setError('Failed to delete image');
    }
  };

  const handleBulkDelete = async (filenames) => {
    if (!selectedProject || filenames.length === 0) return;
    if (!window.confirm(`Delete ${filenames.length} image(s)?`)) return;
    try {
      setLoading(true);
      await axios.post(`${API_URL}/uploads/${selectedProject.id}/delete-many`, {
        filenames,
      });
      setSuccess(`Deleted ${filenames.length} image(s)`);
      await refreshAll(selectedProject.id);
    } catch (err) {
      setError('Failed to delete images');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Delete this project and all its images?')) return;
    try {
      await axios.delete(`${API_URL}/projects/${projectId}`);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      if (selectedProject?.id === projectId) setSelectedProject(null);
      setSuccess('Project deleted');
      fetchStats();
    } catch (err) {
      setError('Failed to delete project');
    }
  };

  const handleRestore = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/projects/restore`);
      setSuccess(response.data.message);
      await fetchProjects();
      fetchStats();
    } catch (err) {
      setError('Failed to restore projects from disk');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API_URL}/auth/logout`).catch(() => {});
    setUser(null);
    setProjects([]);
    setImages([]);
    setSelectedProject(null);
    setStats(null);
  };

  const filteredProjects = useMemo(() => {
    const q = projectQuery.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q)
    );
  }, [projects, projectQuery]);

  if (!authReady) {
    return (
      <div className="login-shell">
        <p className="login-copy">Checking session…</p>
      </div>
    );
  }

  if (!user) {
    return <Login onSuccess={(name) => setUser(name)} />;
  }

  const projectStorage = images.reduce((sum, image) => sum + (image.size || 0), 0);

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-word">Atelier</span>
          <span className="brand-kicker">CDN</span>
        </div>

        <div className="health-row">
          <span className={`health-pill ${stats?.database ? 'ok' : ''}`}>
            <span className="health-dot" />
            Postgres
          </span>
          <span className={`health-pill ${stats?.imagePipeline ? 'ok' : ''}`}>
            <span className="health-dot" />
            Pipeline {stats?.imagePipeline || '—'}
          </span>
        </div>

        <div className="stats-strip">
          <div className="stat">
            <b>{stats?.projectCount ?? projects.length}</b>
            <span>Projects</span>
          </div>
          <div className="stat">
            <b>{stats?.imageCount ?? 0}</b>
            <span>Assets</span>
          </div>
          <div className="stat">
            <b>{formatBytes(stats?.storageBytes || 0)}</b>
            <span>Storage</span>
          </div>
        </div>

        <div className="sidebar-actions">
          <button
            type="button"
            className="btn btn-accent btn-block"
            onClick={() => {
              setShowCreateForm((v) => !v);
              setEditingProject(null);
            }}
          >
            {showCreateForm ? 'Cancel' : 'New project'}
          </button>
        </div>
        <button type="button" className="btn btn-block" onClick={handleRestore} disabled={loading}>
          Restore from disk
        </button>
        <button type="button" className="btn btn-block" onClick={handleLogout}>
          Sign out
        </button>

        {editingProject && (
          <div className="sidebar-form">
            <h3 className="sidebar-form-title">Rename project</h3>
            <ProjectForm
              initialValues={{
                name: editingProject.name,
                description: editingProject.description || '',
              }}
              onSubmit={handleProjectUpdate}
              onCancel={() => setEditingProject(null)}
              submitLabel="Save"
              loading={loading}
            />
          </div>
        )}

        {showCreateForm && !editingProject && (
          <div className="sidebar-form">
            <ProjectForm onSubmit={handleProjectCreate} loading={loading} />
          </div>
        )}

        <div className="sidebar-projects">
          <h2 className="sidebar-heading">Library</h2>
          <input
            className="search-input"
            value={projectQuery}
            onChange={(e) => setProjectQuery(e.target.value)}
            placeholder="Search projects"
            aria-label="Search projects"
          />
          {loading && projects.length === 0 ? (
            <p className="empty-hint">Loading…</p>
          ) : filteredProjects.length === 0 ? (
            <p className="empty-hint">No matching projects</p>
          ) : (
            <ProjectList
              projects={filteredProjects}
              selectedProject={selectedProject}
              onSelectProject={setSelectedProject}
              onRenameProject={handleStartRename}
              onDeleteProject={handleDeleteProject}
            />
          )}
        </div>
      </aside>

      <div className="main">
        <header className="main-header">
          <div>
            <p className="page-kicker">Image delivery</p>
            <h2 className="page-title">
              {selectedProject ? selectedProject.name : 'Select a library'}
            </h2>
            {selectedProject?.description && (
              <p className="page-subtitle">{selectedProject.description}</p>
            )}
          </div>
          {selectedProject && (
            <div className="header-actions">
              <button
                type="button"
                className="btn"
                onClick={() => handleStartRename(selectedProject)}
              >
                Rename
              </button>
              <span className="health-pill">
                {images.length} files · {formatBytes(projectStorage)}
              </span>
            </div>
          )}
        </header>

        {(error || success) && (
          <div className={`toast ${error ? 'toast-error' : 'toast-success'}`} role="alert">
            <span>{error || success}</span>
            <button type="button" className="toast-close" onClick={dismissToast} aria-label="Dismiss">
              ×
            </button>
          </div>
        )}

        <main className="main-content">
          {!selectedProject ? (
            <section className="empty-state">
              <h3>Open a project</h3>
              <p>
                Choose a library from the left, or create one. Upload originals once — serve
                resized WebP/JPEG URLs to your sites.
              </p>
            </section>
          ) : (
            <>
              <section className="panel">
                <h3 className="panel-title">Ingest</h3>
                <ImageUpload
                  onUpload={handleImageUpload}
                  loading={loading}
                  progress={uploadProgress}
                />
              </section>
              <section className="panel">
                <ImageGallery
                  images={images}
                  onDeleteImage={handleDeleteImage}
                  onBulkDelete={handleBulkDelete}
                  loading={loading}
                />
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
